import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ContentType, ApiCustomView, ApiPendingCustomView } from '@/types/api'
import type { MediaPreloadProgress } from '@/services/mediaPreloader'
import { DARK_MODE_ENABLED, DEFAULT_THEME_MODE, type ThemePreference } from '@/theme/themeMode'

interface AppState {
  // Filter Bar
  selectedModelViewId: number | null
  selectedLevelId: number | null
  levelFilterExact: boolean
  selectedContentTagIds: number[]
  searchFilteredOnly: boolean
  setSelectedModelViewId: (id: number | null) => void
  setSelectedLevelId: (id: number | null) => void
  setLevelFilterExact: (exact: boolean) => void
  setSelectedContentTagIds: (ids: number[]) => void
  toggleContentTagId: (id: number) => void
  setSearchFilteredOnly: (val: boolean) => void

  // Custom View selection (mutually exclusive with specific model view selection)
  selectedCustomViewId: number | null
  setSelectedCustomViewId: (id: number | null) => void

  // Loaded custom views (fetched after login / initial sync)
  customViews: ApiCustomView[]
  setCustomViews: (views: ApiCustomView[]) => void

  // Cross-domain shares awaiting acceptance. Online-only, so never persisted.
  pendingCustomViews: ApiPendingCustomView[]
  setPendingCustomViews: (views: ApiPendingCustomView[]) => void

  // Language
  selectedLanguage: string
  setSelectedLanguage: (lang: string) => void
  /**
   * True once the first-run language prompt has been answered in this session. Ephemeral on
   * purpose: the durable answer is the user's stored PreferredLanguageCode, which the server
   * reports back as hasLanguagePreference on the next session.
   */
  languagePromptResolved: boolean
  setLanguagePromptResolved: (resolved: boolean) => void

  // Color theme. themePreference is the user's persisted choice (light, dark or follow the
  // system); resolveThemeMode() decides what is actually applied (light whenever
  // DARK_MODE_ENABLED is off).
  themePreference: ThemePreference
  setThemePreference: (preference: ThemePreference) => void
  /** True while a surface that only supports light mode (the admin panel) is mounted. Ephemeral. */
  forceLightTheme: boolean
  setForceLightTheme: (force: boolean) => void

  // Offline Mode
  isOffline: boolean
  isTrulyOffline: boolean
  isManualOffline: boolean
  setManualOffline: (offline: boolean) => void
  setTrulyOffline: (offline: boolean) => void

  // Offline Session Timer
  offlineSessionStart: number | null
  offlineSessionExpired: boolean
  offlineSessionId: string | null
  setOfflineSessionStart: (ts: number | null) => void
  setOfflineSessionExpired: (expired: boolean) => void
  setOfflineSessionId: (id: string | null) => void

  // Navigation
  activeSidebarNav: string
  setActiveSidebarNav: (nav: string) => void
  selectedPracticeAreaId: number | null
  setSelectedPracticeAreaId: (id: number | null) => void

  // Sidebar collapsed
  sidebarCollapsed: boolean
  toggleSidebarCollapsed: () => void

  // Desktop sidebar width chosen by dragging its edge. Null means the CSS default.
  sidebarWidth: number | null
  setSidebarWidth: (width: number | null) => void

  // Mobile navigation drawer. Deliberately NOT persisted: a drawer that reopens
  // itself on reload is a bug, not a restored preference.
  mobileNavOpen: boolean
  setMobileNavOpen: (open: boolean) => void

  // Sidebar Tree
  treeFilterText: string
  setTreeFilterText: (text: string) => void
  expandedPracticeAreaIds: Set<number>
  togglePracticeAreaExpanded: (id: number) => void
  expandAllPracticeAreas: (ids: number[]) => void
  collapseAllPracticeAreas: () => void

  // Lightbox state
  lightboxSrc: string | null
  setLightboxSrc: (src: string | null) => void

  // Note editor state
  noteEditorOpen: { contentType: ContentType; entityId: number } | null
  setNoteEditorOpen: (target: { contentType: ContentType; entityId: number } | null) => void
  notesAutosave: boolean
  setNotesAutosave: (enabled: boolean) => void
  /** Scale factor applied to practice and practice area content only. */
  contentZoom: number
  setContentZoom: (zoom: number) => void
  /** Glossary page shows each term's default-language term beside the translated one. */
  showGlossarySourceTerms: boolean
  setShowGlossarySourceTerms: (show: boolean) => void
  /** Remembers whether the note panel was last shown docked or as the floating window. */
  notesPoppedOut: boolean
  setNotesPoppedOut: (popped: boolean) => void
  /** Last position and size of the popped-out note window, in viewport pixels. */
  notesWindowRect: { x: number; y: number; width: number; height: number } | null
  setNotesWindowRect: (rect: { x: number; y: number; width: number; height: number } | null) => void
  /** Height of the docked note panel, in pixels (clamped 200–800). */
  notesDockedHeight: number
  setNotesDockedHeight: (height: number) => void

  // Page overlay (full-screen modal for Overview, Appendices, Glossary, Release Notes, User Content, Crosswalks)
  overlayPage: { type: 'overview' | 'appendices' | 'glossary' | 'release-notes' | 'user-content' | 'faq' | 'crosswalks' | 'media-gallery'; hash?: string } | null
  setOverlayPage: (page: { type: 'overview' | 'appendices' | 'glossary' | 'release-notes' | 'user-content' | 'faq' | 'crosswalks' | 'media-gallery'; hash?: string } | null) => void

  // PDF viewer state (ephemeral — not persisted)
  pdfViewer: { systemFileName: string; title: string } | null
  setPdfViewer: (pdf: { systemFileName: string; title: string } | null) => void

  // Unsaved note text. Deliberately NOT persisted: an editor buffer does not survive a reload,
  // so a flag saying otherwise would block navigation forever after a crash.
  notesDirty: boolean
  setNotesDirty: (dirty: boolean) => void
  /** The action a confirmation dialog is currently holding back. Null when nothing is pending. */
  pendingGuardedAction: (() => void) | null
  /** Runs `action` now, or defers it behind a confirmation when there is unsaved note text. */
  requestGuardedAction: (action: () => void) => void
  resolveGuardedAction: (proceed: boolean) => void

  // Draft Content Preview. When on, draft content is rendered. showContentDiff then chooses HOW:
  // an inline diff against the published version (true) or the draft rendered in situ (false).
  showDraftContent: boolean
  setShowDraftContent: (show: boolean) => void
  showContentDiff: boolean
  setShowContentDiff: (show: boolean) => void

  // Incremented by useInitialSync after user data is written to IndexedDB,
  // so hooks like useNotes / useBookmarks can re-read the fresh cache.
  userDataSyncVersion: number
  bumpUserDataSyncVersion: () => void

  // Ephemeral progress for background image pre-caching. Never persisted.
  imageCacheProgress: MediaPreloadProgress | null
  setImageCacheProgress: (p: MediaPreloadProgress) => void
  clearImageCacheProgress: () => void

  // Ephemeral flag: true while a force-refresh of the content bundle is in progress.
  isBundleRefreshing: boolean
  setBundleRefreshing: (v: boolean) => void
}

export const useAppStore = create<AppState>()(
  persist(
     (set, get) => ({
       // Filter bar
       selectedModelViewId: null,
       selectedLevelId: null,
       levelFilterExact: false,
       selectedContentTagIds: [-1],
       searchFilteredOnly: false,
      setSelectedModelViewId: (id) => set({ selectedModelViewId: id }),
      setSelectedLevelId: (id) => set({ selectedLevelId: id }),
      setLevelFilterExact: (exact) => set({ levelFilterExact: exact }),
      setSelectedContentTagIds: (ids) => set({ selectedContentTagIds: ids.length === 0 ? [-1] : ids }),
      toggleContentTagId: (id) => {
        const current = get().selectedContentTagIds
        const next = current.includes(id)
          ? current.filter((x) => x !== id)
          : [...current, id]
        set({ selectedContentTagIds: next.length === 0 ? [-1] : next })
      },
      setSearchFilteredOnly: (val) => set({ searchFilteredOnly: val }),

      // Custom View selection
      selectedCustomViewId: null,
      setSelectedCustomViewId: (id) => {
        if (id !== null) {
          // When selecting a custom view, reset model view selection to "all"
          set({ selectedCustomViewId: id, selectedModelViewId: null })
        } else {
          set({ selectedCustomViewId: null })
        }
      },

      // Loaded custom views
      customViews: [],
      setCustomViews: (views) =>
        set((state) => ({
          customViews: views,
          // A revoked, left or deleted view must not stay selected.
          selectedCustomViewId:
            state.selectedCustomViewId !== null && !views.some((v) => v.id === state.selectedCustomViewId)
              ? null
              : state.selectedCustomViewId,
        })),
      pendingCustomViews: [],
      setPendingCustomViews: (views) => set({ pendingCustomViews: views }),

      // Language
      selectedLanguage: 'en',
      setSelectedLanguage: (lang) => set({ selectedLanguage: lang }),
      languagePromptResolved: false,
      setLanguagePromptResolved: (resolved) => set({ languagePromptResolved: resolved }),

      // Color theme. The setter is a no-op while dark mode is disabled, so a stale handler
      // can never change the stored choice behind the user's back.
      themePreference: DEFAULT_THEME_MODE,
      setThemePreference: (preference) => {
        if (!DARK_MODE_ENABLED) return
        set({ themePreference: preference })
      },
      // Seeded from the URL so a cold load straight into /admin never renders dark before
      // AdminShell (lazy) mounts and takes over.
      forceLightTheme: typeof window !== 'undefined' && window.location.pathname.startsWith('/admin'),
      setForceLightTheme: (force) => set({ forceLightTheme: force }),

      // Offline mode.
      // isTrulyOffline / isOffline are initialised synchronously from navigator.onLine so
      // that the very first render already reflects the real connectivity state instead of
      // waiting for the useConnectivity useEffect to fire after mount.
      // isManualOffline is persisted (see partialize below) so the user's explicit choice
      // survives a full browser/PWA restart.
      isTrulyOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
      isManualOffline: false,
      isOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
      setManualOffline: (offline) => {
        const isTrulyOffline = get().isTrulyOffline
        set({
          isManualOffline: offline,
          isOffline: offline || isTrulyOffline,
          // Draft content requires a live connection — disable it when going offline.
          ...(offline && { showDraftContent: false }),
        })
      },
      setTrulyOffline: (offline) => {
        const isManualOffline = get().isManualOffline
        set({
          isTrulyOffline: offline,
          isOffline: offline || isManualOffline,
          // Draft content requires a live connection — disable it when going offline.
          ...(offline && { showDraftContent: false }),
        })
      },

      // Offline Session Timer
      offlineSessionStart: null,
      offlineSessionExpired: false,
      offlineSessionId: null,
      setOfflineSessionStart: (ts) => set({ offlineSessionStart: ts }),
      setOfflineSessionExpired: (expired) => set({ offlineSessionExpired: expired }),
      setOfflineSessionId: (id) => set({ offlineSessionId: id }),

      // Navigation
      activeSidebarNav: 'tree',
      setActiveSidebarNav: (nav) => set({ activeSidebarNav: nav }),
      selectedPracticeAreaId: null,
      setSelectedPracticeAreaId: (id) => set({ selectedPracticeAreaId: id }),

      // Sidebar collapsed
      sidebarCollapsed: false,
      toggleSidebarCollapsed: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      sidebarWidth: null,
      setSidebarWidth: (width) => set({ sidebarWidth: width }),

      // Mobile navigation drawer (not persisted)
      mobileNavOpen: false,
      setMobileNavOpen: (open) => set({ mobileNavOpen: open }),

      // Tree
      treeFilterText: '',
      setTreeFilterText: (text) => set({ treeFilterText: text }),
      expandedPracticeAreaIds: new Set<number>(),
      togglePracticeAreaExpanded: (id) => {
        const current = new Set(get().expandedPracticeAreaIds)
        if (current.has(id)) {
          current.delete(id)
        } else {
          current.add(id)
        }
        set({ expandedPracticeAreaIds: current })
      },
      expandAllPracticeAreas: (ids) =>
        set({ expandedPracticeAreaIds: new Set(ids) }),
      collapseAllPracticeAreas: () =>
        set({ expandedPracticeAreaIds: new Set<number>() }),

      // Lightbox (ephemeral — not persisted)
      lightboxSrc: null,
      setLightboxSrc: (src) => set({ lightboxSrc: src }),

      // Note editor (ephemeral — not persisted)
      noteEditorOpen: null,
      setNoteEditorOpen: (target) => set({ noteEditorOpen: target }),

      // Notes autosave preference (persisted)
      notesAutosave: false,
      setNotesAutosave: (enabled) => set({ notesAutosave: enabled }),

      // Content zoom (persisted)
      contentZoom: 1,
      setContentZoom: (zoom) => set({ contentZoom: zoom }),

      // Glossary source-term display preference (persisted)
      showGlossarySourceTerms: false,
      setShowGlossarySourceTerms: (show) => set({ showGlossarySourceTerms: show }),

      // Notes docked-vs-popped-out preference (persisted)
      notesPoppedOut: false,
      setNotesPoppedOut: (popped) => set({ notesPoppedOut: popped }),

      // Popped-out note window geometry (persisted)
      notesWindowRect: null,
      setNotesWindowRect: (rect) => set({ notesWindowRect: rect }),

      // Docked note panel height (persisted)
      notesDockedHeight: 220,
      setNotesDockedHeight: (height) => set({ notesDockedHeight: height }),

      // Page overlay (ephemeral — not persisted)
      overlayPage: null,
      setOverlayPage: (page) => set({ overlayPage: page }),

      // PDF viewer (ephemeral — not persisted)
      pdfViewer: null,
      setPdfViewer: (pdf) => set({ pdfViewer: pdf }),

      notesDirty: false,
      setNotesDirty: (dirty) => set({ notesDirty: dirty }),
      pendingGuardedAction: null,
      requestGuardedAction: (action) => {
        if (!get().notesDirty) {
          action()
          return
        }
        set({ pendingGuardedAction: action })
      },
      resolveGuardedAction: (proceed) => {
        const action = get().pendingGuardedAction
        set({ pendingGuardedAction: null })
        if (!proceed) return
        // Discarding is what the user just confirmed, so the flag clears before the action runs
        // — otherwise the note panel's own teardown would re-open this same dialog.
        set({ notesDirty: false })
        action?.()
      },

      // Draft Content Preview (persisted so preference survives reload)
      showDraftContent: false,
      setShowDraftContent: (show) => set({ showDraftContent: show }),
      // Diff vs in-situ view for draft content. Defaults to diff (the established behavior).
      showContentDiff: true,
      setShowContentDiff: (show) => set({ showContentDiff: show }),

      // User data sync signal (ephemeral — not persisted)
      userDataSyncVersion: 0,
      bumpUserDataSyncVersion: () =>
        set((s) => ({ userDataSyncVersion: s.userDataSyncVersion + 1 })),

      // Image cache progress (ephemeral — not persisted)
      imageCacheProgress: null,
      setImageCacheProgress: (p) => set({ imageCacheProgress: p }),
      clearImageCacheProgress: () => set({ imageCacheProgress: null }),

      // Bundle refreshing flag (ephemeral — not persisted)
      isBundleRefreshing: false,
      setBundleRefreshing: (v) => set({ isBundleRefreshing: v }),
    }),
    {
      name: 'cmmi-viewer-app-state',
      partialize: (state) => ({
        selectedLanguage: state.selectedLanguage,
        themePreference: state.themePreference,
        selectedModelViewId: state.selectedModelViewId,
        selectedLevelId: state.selectedLevelId,
        selectedContentTagIds: state.selectedContentTagIds,
        selectedCustomViewId: state.selectedCustomViewId,
        searchFilteredOnly: state.searchFilteredOnly,
        // Persist the manual offline toggle so the user's choice survives a restart.
        isManualOffline: state.isManualOffline,
        // Persist draft preview preference so the toggle survives a page reload.
        showDraftContent: state.showDraftContent,
        // Persist the diff-vs-in-situ preference so it survives a page reload.
        showContentDiff: state.showContentDiff,
        // Persist autosave preference so the checkbox state survives a page reload.
        notesAutosave: state.notesAutosave,
        contentZoom: state.contentZoom,
        showGlossarySourceTerms: state.showGlossarySourceTerms,
        // Persist the note display mode so a popped-out note reopens popped out.
        notesPoppedOut: state.notesPoppedOut,
        // Persist the floating note window geometry so it reopens where it was left.
        notesWindowRect: state.notesWindowRect,
        // Persist the docked panel height so the flyout reopens at the chosen size.
        notesDockedHeight: state.notesDockedHeight,
        // Persist the dragged sidebar width so the layout survives a page reload.
        sidebarWidth: state.sidebarWidth,
        // Persist timer state so cold-start restores timer position and lockout status.
        offlineSessionStart: state.offlineSessionStart,
        offlineSessionExpired: state.offlineSessionExpired,
        offlineSessionId: state.offlineSessionId,
      }),
    }
  )
)

// After the persisted slice is merged back in, recompute isOffline so that it
// correctly reflects both the restored isManualOffline AND the real network state.
// We use onFinishHydration (not onRehydrateStorage) because only onFinishHydration
// allows us to call setState and trigger proper React re-renders.
useAppStore.persist.onFinishHydration(() => {
  const { isManualOffline } = useAppStore.getState()
  const trulyOffline = typeof navigator !== 'undefined' ? !navigator.onLine : false
  const isOffline = trulyOffline || isManualOffline
  useAppStore.setState({
    isTrulyOffline: trulyOffline,
    isOffline,
    // Draft content requires a live connection — clear the persisted preference if the
    // app is restored while offline so the toggle doesn't show stale draft data.
    ...(isOffline && { showDraftContent: false }),
  })
})
